import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  MapPin, BarChart3, TrendingUp, TrendingDown, AlertTriangle, 
  Eye, Filter, Navigation, Target, Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const GeoChannelAnalysis = () => {
  const navigate = useNavigate();

  const channelData = [
    {
      name: 'Digital',
      planned: 6000000,
      actual: 6850000,
      percentage: 38.5,
      performance: 114.2,
      growth: 28.3,
      status: 'exceeding',
      metrics: {
        cpa: 45.20,
        roas: 4.1,
        impressions: 24.5,
        ctr: 3.2
      },
      color: 'from-violet-400 to-purple-500'
    },
    {
      name: 'TV',
      planned: 5400000,
      actual: 4950000,
      percentage: 27.8,
      performance: 91.7,
      growth: 15.6,
      status: 'underperforming',
      metrics: {
        grp: 145,
        trp: 128,
        reach: 4.2,
        frequency: 2.8
      },
      color: 'from-blue-400 to-indigo-500'
    },
    {
      name: 'Outdoor',
      planned: 3600000,
      actual: 3240000,
      percentage: 18.2,
      performance: 90.0,
      growth: 12.1,
      status: 'underperforming',
      metrics: {
        traffic: 185,
        impressions: 12.8,
        visibility: 87,
        locations: 42
      },
      color: 'from-emerald-400 to-green-500'
    },
    {
      name: 'Radio',
      planned: 1800000,
      actual: 1620000,
      percentage: 9.1,
      performance: 90.0,
      growth: 8.7,
      status: 'underperforming',
      metrics: {
        listeners: 2.1,
        spots: 3.5,
        primetime: 68,
        share: 12.4
      },
      color: 'from-orange-400 to-red-500'
    },
    {
      name: 'Print',
      planned: 1200000,
      actual: 1140000,
      percentage: 6.4,
      performance: 95.0,
      growth: 4.2,
      status: 'on-track',
      metrics: {
        circulation: 1.2,
        readership: 2.8,
        cpm: 85.40,
        roas: 1.8
      },
      color: 'from-amber-400 to-yellow-500'
    }
  ];

  const geographicData = [
    {
      region: 'UAE',
      coordinates: { lat: 25.2048, lng: 55.2708 },
      performance: 118.5,
      spend: 6300000,
      channels: ['Digital', 'TV', 'Outdoor'],
      topChannel: 'Digital',
      growth: 18.5,
      status: 'excellent'
    },
    {
      region: 'Saudi Arabia',
      coordinates: { lat: 23.8859, lng: 45.0792 },
      performance: 92.3,
      spend: 6120000,
      channels: ['TV', 'Digital', 'Radio'],
      topChannel: 'TV',
      growth: 15.2,
      status: 'good'
    },
    {
      region: 'Kuwait',
      coordinates: { lat: 29.3117, lng: 47.4818 },
      performance: 88.7,
      spend: 1440000,
      channels: ['Outdoor', 'Digital'],
      topChannel: 'Digital',
      growth: 12.8,
      status: 'needs-improvement'
    },
    {
      region: 'Qatar',
      coordinates: { lat: 25.3548, lng: 51.1839 },
      performance: 105.2,
      spend: 1620000,
      channels: ['Digital', 'Outdoor'],
      topChannel: 'Digital',
      growth: 16.1,
      status: 'good'
    },
    {
      region: 'Bahrain',
      coordinates: { lat: 25.9304, lng: 50.6378 },
      performance: 110.8,
      spend: 1080000,
      channels: ['Digital', 'TV'],
      topChannel: 'Digital',
      growth: 14.3,
      status: 'excellent'
    }
  ];

  const handleChannelClick = (channelName) => {
    // Navigate to a detailed channel page
    navigate(`/channel-details/${channelName.toLowerCase()}`, {
      state: {
        channelName,
        data: channelData.find(c => c.name === channelName)
      }
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'exceeding': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'on-track': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'underperforming': return 'text-red-600 bg-red-50 border-red-200';
      case 'excellent': return 'text-emerald-600 bg-emerald-50';
      case 'good': return 'text-blue-600 bg-blue-50';
      case 'needs-improvement': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Card className="shadow-lg border-l-4 border-l-emerald-500">
      <CardHeader>
        <CardTitle className="text-xl font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
          Media Mix & Geographic Performance Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="channels" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="channels" className="flex items-center space-x-2">
              <BarChart3 className="h-4 w-4" />
              <span>Channel Performance</span>
            </TabsTrigger>
            <TabsTrigger value="mediamix" className="flex items-center space-x-2">
              <Target className="h-4 w-4" />
              <span>Media Mix Analysis</span>
            </TabsTrigger>
            <TabsTrigger value="geographic" className="flex items-center space-x-2">
              <MapPin className="h-4 w-4" />
              <span>Geographic Analysis</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="channels" className="space-y-4">
            <div className="grid gap-4">
              {channelData.map((channel, index) => (
                <div
                  key={index}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200 cursor-pointer group"
                  onClick={() => handleChannelClick(channel.name)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${channel.color}`}></div>
                      <div>
                        <h4 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                          {channel.name}
                        </h4>
                        <div className="text-sm text-gray-500">
                          {channel.percentage.toFixed(1)}% of total spend
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Badge className={getStatusColor(channel.status)}>
                        {channel.status.replace('-', ' ')}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Details
                      </Button>
                    </div>
                  </div>

                  {/* Performance Bar */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Performance vs Plan</span>
                      <span className={`font-semibold ${
                        channel.performance > 100 ? 'text-emerald-600' : 
                        channel.performance > 95 ? 'text-blue-600' : 'text-red-600'
                      }`}>
                        {channel.performance.toFixed(1)}%
                      </span>
                    </div>
                    
                    <div className="relative">
                      {/* Planned Bar (Background) */}
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-gray-400 h-3 rounded-full" 
                          style={{ width: '100%' }}
                        ></div>
                      </div>
                      
                      {/* Actual Bar (Overlay) */}
                      <div className="absolute top-0 w-full">
                        <div 
                          className={`h-3 rounded-full transition-all duration-500 ${
                            channel.performance > 100 
                              ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' 
                              : channel.performance > 95
                              ? 'bg-gradient-to-r from-blue-400 to-blue-500'
                              : 'bg-gradient-to-r from-red-400 to-red-500'
                          } ${channel.performance > 100 ? 'animate-pulse' : ''}`}
                          style={{ 
                            width: `${Math.min(channel.performance, 120)}%`,
                            ...(channel.performance > 100 && {
                              boxShadow: '0 0 10px rgba(16, 185, 129, 0.5)'
                            })
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Spend Details */}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        Actual: {formatCurrency(channel.actual)} / Planned: {formatCurrency(channel.planned)}
                      </span>
                      <span className={`font-semibold ${channel.growth > 15 ? 'text-emerald-600' : 'text-gray-600'}`}>
                        +{channel.growth.toFixed(1)}% growth
                      </span>
                    </div>

                    {/* Industry-Specific Metrics */}
                    <div className="grid grid-cols-4 gap-3 pt-2 border-t border-gray-100">
                      {channel.name === 'TV' && (
                        <>
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900">{channel.metrics.grp}</div>
                            <div className="text-xs text-gray-500">GRP</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600">{channel.metrics.trp}</div>
                            <div className="text-xs text-gray-500">TRP</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-emerald-600">{channel.metrics.reach.toFixed(1)}M</div>
                            <div className="text-xs text-gray-500">Reach</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-purple-600">{channel.metrics.frequency.toFixed(1)}</div>
                            <div className="text-xs text-gray-500">Frequency</div>
                          </div>
                        </>
                      )}
                      {channel.name === 'Digital' && (
                        <>
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900">${channel.metrics.cpa.toFixed(2)}</div>
                            <div className="text-xs text-gray-500">CPA</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-emerald-600">{channel.metrics.roas.toFixed(1)}x</div>
                            <div className="text-xs text-gray-500">ROAS</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600">{channel.metrics.impressions.toFixed(1)}M</div>
                            <div className="text-xs text-gray-500">Impressions</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-purple-600">{channel.metrics.ctr.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500">CTR</div>
                          </div>
                        </>
                      )}
                      {channel.name === 'Outdoor' && (
                        <>
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900">{channel.metrics.traffic}K</div>
                            <div className="text-xs text-gray-500">Daily Traffic</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-emerald-600">{channel.metrics.impressions.toFixed(1)}M</div>
                            <div className="text-xs text-gray-500">Impressions</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600">{channel.metrics.visibility}%</div>
                            <div className="text-xs text-gray-500">Visibility</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-purple-600">{channel.metrics.locations}</div>
                            <div className="text-xs text-gray-500">Locations</div>
                          </div>
                        </>
                      )}
                      {channel.name === 'Radio' && (
                        <>
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900">{channel.metrics.listeners.toFixed(1)}M</div>
                            <div className="text-xs text-gray-500">Listeners</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-emerald-600">{channel.metrics.spots.toFixed(1)}</div>
                            <div className="text-xs text-gray-500">Avg Spots</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600">{channel.metrics.primetime}%</div>
                            <div className="text-xs text-gray-500">Prime Time</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-purple-600">{channel.metrics.share.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500">Market Share</div>
                          </div>
                        </>
                      )}
                      {channel.name === 'Print' && (
                        <>
                          <div className="text-center">
                            <div className="text-lg font-bold text-gray-900">{channel.metrics.circulation.toFixed(1)}M</div>
                            <div className="text-xs text-gray-500">Circulation</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-emerald-600">{channel.metrics.readership.toFixed(1)}M</div>
                            <div className="text-xs text-gray-500">Readership</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600">${channel.metrics.cpm.toFixed(2)}</div>
                            <div className="text-xs text-gray-500">CPM</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-purple-600">{channel.metrics.roas.toFixed(1)}x</div>
                            <div className="text-xs text-gray-500">ROAS</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="mediamix" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Media Mix Pie Chart */}
              <Card className="p-6">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900 mb-4">Media Channel Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <svg width="280" height="280" viewBox="0 0 200 200" className="mx-auto">
                      {(() => {
                        let cumulativePercentage = 0;
                        return channelData.map((channel, index) => {
                          const startAngle = cumulativePercentage * 3.6;
                          const endAngle = (cumulativePercentage + channel.percentage) * 3.6;
                          const largeArcFlag = channel.percentage > 50 ? 1 : 0;
                          
                          const x1 = 100 + 80 * Math.cos((startAngle - 90) * Math.PI / 180);
                          const y1 = 100 + 80 * Math.sin((startAngle - 90) * Math.PI / 180);
                          const x2 = 100 + 80 * Math.cos((endAngle - 90) * Math.PI / 180);
                          const y2 = 100 + 80 * Math.sin((endAngle - 90) * Math.PI / 180);
                          
                          const pathData = [
                            "M", 100, 100,
                            "L", x1, y1,
                            "A", 80, 80, 0, largeArcFlag, 1, x2, y2,
                            "Z"
                          ].join(" ");
                          
                          cumulativePercentage += channel.percentage;
                          
                          const colors = [
                            'fill-violet-500',      // Digital
                            'fill-indigo-500',      // TV
                            'fill-emerald-500',     // Outdoor
                            'fill-orange-500',      // Radio
                            'fill-amber-500'        // Print
                          ];
                          
                          return (
                            <path
                              key={index}
                              d={pathData}
                              className={`${colors[index]} hover:opacity-80 cursor-pointer transition-all duration-200 hover:scale-105`}
                              stroke="white"
                              strokeWidth="2"
                              onClick={() => handleChannelClick(channel.name)}
                            />
                          );
                        });
                      })()}
                      
                      {/* Center circle for clean look */}
                      <circle cx="100" cy="100" r="35" fill="white" stroke="#e5e7eb" strokeWidth="2" />
                    </svg>
                    
                    {/* Legend */}
                    <div className="grid grid-cols-1 gap-2 mt-6">
                      {channelData.map((channel, index) => (
                        <div 
                          key={index} 
                          className="flex justify-between items-center p-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:shadow-md transition-all duration-200 cursor-pointer group"
                          onClick={() => handleChannelClick(channel.name)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-4 h-4 rounded shadow-sm ${
                              index === 0 ? 'bg-violet-500' :
                              index === 1 ? 'bg-indigo-500' :
                              index === 2 ? 'bg-emerald-500' :
                              index === 3 ? 'bg-orange-500' : 'bg-amber-500'
                            }`}></div>
                            <div>
                              <div className="font-medium text-gray-900 group-hover:text-emerald-600 transition-colors">{channel.name}</div>
                              <div className="text-sm text-gray-600">{formatCurrency(channel.actual)}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-lg">{channel.percentage.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500 flex items-center">
                              <Eye className="h-3 w-3 mr-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                              of total
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Summary */}
              <Card className="p-6">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900 mb-4">Channel Performance Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {channelData.map((channel, index) => (
                      <div key={index} className="p-3 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-900">{channel.name}</span>
                          <Badge className={getStatusColor(channel.status)}>
                            {channel.status.replace('-', ' ')}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Performance:</span>
                            <span className={`ml-2 font-semibold ${
                              channel.performance > 100 ? 'text-emerald-600' : 
                              channel.performance > 95 ? 'text-blue-600' : 'text-red-600'
                            }`}>
                              {channel.performance.toFixed(1)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Growth:</span>
                            <span className="ml-2 font-semibold text-emerald-600">+{channel.growth.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="geographic" className="space-y-4">
            {/* Google Maps Integration Placeholder */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-6 border border-emerald-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">MENA Region Performance Map</h4>
                <Button variant="outline" size="sm">
                  <Navigation className="h-4 w-4 mr-2" />
                  Interactive Map
                </Button>
              </div>
              
              {/* Map Simulation */}
              <div className="h-64 bg-white rounded-lg border-2 border-dashed border-emerald-300 flex items-center justify-center mb-4">
                <div className="text-center text-emerald-600">
                  <MapPin className="h-12 w-12 mx-auto mb-2" />
                  <div className="font-medium">Google Maps Integration</div>
                  <div className="text-sm text-emerald-500">Geographic performance visualization</div>
                </div>
              </div>

              {/* Regional Performance Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {geographicData.map((region, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h5 className="font-semibold text-gray-900">{region.region}</h5>
                        <div className="text-sm text-gray-500">Top: {region.topChannel}</div>
                      </div>
                      <Badge className={getStatusColor(region.status)}>
                        {region.status.replace('-', ' ')}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Performance:</span>
                        <span className={`font-semibold ${
                          region.performance > 105 ? 'text-emerald-600' : 
                          region.performance > 95 ? 'text-blue-600' : 'text-red-600'
                        }`}>
                          {region.performance.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Spend:</span>
                        <span className="font-semibold">{formatCurrency(region.spend)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Growth:</span>
                        <span className="font-semibold text-emerald-600">+{region.growth.toFixed(2)}%</span>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-2">Active Channels:</div>
                      <div className="flex flex-wrap gap-1">
                        {region.channels.map((channel, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {channel}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default GeoChannelAnalysis;